import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import moment from "moment";
import { getChurnRisk } from "../services/userService";

// Silent-churn view (founder 2026-09-03). Members who joined, never got
// traction (zero engagement: no Vault started, reveal, or chat), and have
// gone quiet past the matcher's liveness gate. The acquisition leak a
// signups number hides. Read-only.
const ChurnRiskLayer = () => {
  const [days, setDays] = useState(14);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async (d) => {
    setLoading(true);
    setErr("");
    try {
      const res = await getChurnRisk({ days: d });
      setRows(res?.data?.data?.rows || []);
    } catch (e) {
      setErr("Could not load the churn view.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const genderLabel = (g) =>
    g === "man" ? "Man" : g === "woman" ? "Woman" : g ? "Beyond binary" : "N/A";

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 cz-toolbar">
        <div className="cz-topline">
          <div>
            <h6 className="mb-1 fw-semibold">Silent churn</h6>
            <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 640 }}>
              Members who <b>finished onboarding</b>, then never got traction (no
              Vault, reveal, or chat) and have gone quiet. Anyone who stopped
              during signup is not counted here: they are a drop on the sign-up
              funnel instead, so the two pages never count the same person twice.
            </p>
          </div>
          <div className="cz-actions">
            <span className="text-sm text-secondary-light">Quiet for at least</span>
            <select
              className="form-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
            </select>
          </div>
        </div>
        <div className="cz-filters">
          <span className="badge bg-neutral-200 text-neutral-600">
            {loading ? "Loading…" : `${rows.length} member${rows.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <div className="card-body p-0">
        {err ? (
          <div className="alert alert-danger m-3">{err}</div>
        ) : (
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Age</th>
                  <th>Gender</th>
                  <th>Area</th>
                  <th>Joined</th>
                  <th>Last seen</th>
                  <th title="Introductions ever dealt to them">Intros ever</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <div className="fw-medium">{r.fullName || "N/A"}</div>
                      <div className="text-sm text-secondary-light">{r.email}</div>
                    </td>
                    <td>{r.age ?? "N/A"}</td>
                    <td>{genderLabel(r.gender)}</td>
                    <td className="text-sm">{r.city || (r.pocketState === "active" ? "Ottawa" : r.pocketState || "N/A")}</td>
                    <td>
                      <span title={moment(r.createdAt).format("DD-MM-YYYY")}>
                        {r.daysSinceJoin}d ago
                      </span>
                    </td>
                    <td>
                      {r.lastSeen ? (
                        <span
                          title={moment(r.lastSeen).format("DD-MM-YYYY HH:mm")}
                          className={r.daysQuiet >= 30 ? "text-danger-main fw-medium" : ""}
                        >
                          {r.daysQuiet}d ago
                        </span>
                      ) : (
                        <span className="text-danger-main fw-medium">never opened</span>
                      )}
                    </td>
                    <td>
                      {r.lifetimePairs === 0 ? (
                        <span className="text-secondary-light">none</span>
                      ) : (
                        r.lifetimePairs
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-secondary-light">
                      <Icon icon="mdi:check-circle-outline" className="me-1" />
                      No silent-churn members at this threshold. Nice.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChurnRiskLayer;
